import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, Ban, BadgeCheck, CheckCircle2, Flag, Loader2, Phone, Mail, Send, Share2, XCircle,
} from 'lucide-react';
import convApi, { newClientMsgId } from '@/lib/conversations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (src) => {
  if (!src) return '';
  if (src.startsWith('data:') || src.startsWith('http')) return src;
  return src.startsWith('/api/') ? `${BACKEND_URL}${src}` : src;
};

const MOTIVOS = [
  ['fraude', 'Tentativa de fraude'],
  ['ofensivo', 'Linguagem ofensiva'],
  ['spam', 'Spam ou publicidade'],
  ['phishing', 'Pedido de códigos ou dados'],
  ['dados_pessoais', 'Partilha indevida de dados'],
  ['pedido_pagamento', 'Pedido de pagamento suspeito'],
  ['outro', 'Outro motivo'],
];

const ESTADO_LABEL = {
  aberta: 'Conversa aberta',
  resolvida: 'Marcada como resolvida',
  encerrada: 'Negociação encerrada',
  bloqueada_moderacao: 'Encerrada pela moderação',
};

const hora = (iso) => (iso ? new Date(iso).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');

export default function ConversationThread({ conversationId, onSessionLost }) {
  const [detail, setDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [texto, setTexto] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [aviso, setAviso] = useState('');
  const [confirmar, setConfirmar] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [share, setShare] = useState({ telefone: false, email: false });
  const [report, setReport] = useState({ open: false, motivo: 'fraude', descricao: '', done: '' });
  const [loading, setLoading] = useState(true);
  const bottom = useRef(null);
  const msgIdRef = useRef(newClientMsgId());

  const fail = (e) => {
    if (e.response?.status === 401) { onSessionLost?.(); return; }
    setErr(e.response?.data?.detail || 'Ocorreu um erro. Tente novamente.');
  };

  const loadAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [d, m] = await Promise.all([
        convApi.get(`/${conversationId}`),
        convApi.get(`/${conversationId}/mensagens?limite=60`),
      ]);
      setDetail(d.data);
      setMessages(m.data.items || []);
      if (!silent) setErr('');
      await convApi.post(`/${conversationId}/ler`, {});
    } catch (e) { fail(e); } finally { if (!silent) setLoading(false); }
  }, [conversationId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    const timer = setInterval(() => loadAll(true), 10000);
    return () => clearInterval(timer);
  }, [loadAll]);

  useEffect(() => { bottom.current?.scrollIntoView({ block: 'end' }); }, [messages.length]);

  const send = async (confirmarPartilha = false) => {
    const value = texto.trim();
    if (!value || busy) return;
    setBusy(true); setErr(''); setAviso('');
    try {
      const res = await convApi.post(`/${conversationId}/mensagens`, {
        texto: value, client_msg_id: msgIdRef.current, confirmar_partilha: confirmarPartilha,
      });
      if (res.data?.requer_confirmacao) { setConfirmar(res.data.aviso); setBusy(false); return; }
      setConfirmar(null);
      setTexto('');
      msgIdRef.current = newClientMsgId();
      try { const { track } = await import('@/lib/analytics'); track('send_message', { conversation: true }); } catch (_) {}
      if (res.data?.aviso) setAviso(res.data.aviso);
      await loadAll(true);
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const act = async (path, body = {}) => {
    setBusy(true); setErr('');
    try { await convApi.post(`/${conversationId}/${path}`, body); await loadAll(true); }
    catch (e) { fail(e); } finally { setBusy(false); }
  };

  const submitShare = async () => {
    setBusy(true);
    try {
      await convApi.post(`/${conversationId}/partilhar-contacto`, share);
      setShowShare(false); setShare({ telefone: false, email: false });
      await loadAll(true);
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const submitReport = async () => {
    setBusy(true);
    try {
      const res = await convApi.post(`/${conversationId}/denuncia`, {
        motivo: report.motivo, descricao: report.descricao,
      });
      setReport((r) => ({ ...r, open: false, descricao: '', done: res.data?.mensagem || 'Denúncia registada.' }));
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="flex items-center gap-2 p-6 text-sm text-slate-500" data-testid="conv-thread-loading">
      <Loader2 className="h-4 w-4 animate-spin" /> A carregar conversa...
    </div>;
  }
  if (!detail) {
    return <div className="p-6 text-sm text-red-600" data-testid="conv-thread-error">{err || 'Conversa não disponível.'}</div>;
  }

  const a = detail.anuncio || {};
  const outro = detail.outro || {};
  const eu = detail.eu || {};

  return (
    <div className="grid gap-3" data-testid="conv-thread">
      <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex items-start gap-3">
          <Link to="/conversas" className="mt-1 text-slate-400 hover:text-slate-700" data-testid="conv-back">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          {a.foto ? (
            <img src={resolveImg(a.foto)} alt={a.titulo} className="h-16 w-24 shrink-0 rounded-lg object-cover" />
          ) : <div className="h-16 w-24 shrink-0 rounded-lg bg-slate-100" />}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-slate-900" data-testid="conv-listing-title">{a.titulo}</p>
            <p className="mt-0.5 text-xs text-slate-500">
              {a.categoria === 'venda' ? 'Viatura à venda' : a.categoria === 'motorista' ? 'Motorista à procura' : 'Viatura para aluguer'}
              {a.preco ? ` · ${a.preco} €${a.categoria === 'aluguer' ? '/semana' : ''}` : ''}
              {a.cidade ? ` · ${a.cidade}` : ''}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-600" data-testid="conv-listing-state">{a.estado}</span>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-600" data-testid="conv-state">{ESTADO_LABEL[detail.estado] || detail.estado}</span>
              {a.url && a.disponivel && <Link to={a.url} className="text-[11px] font-semibold text-emerald-600 underline" data-testid="conv-listing-link">Ver anúncio</Link>}
            </div>
          </div>
        </div>

        <div className="mt-3 rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
          <p className="font-semibold text-slate-800" data-testid="conv-other-name">
            {outro.nome_declarado || outro.nome_publico} <span className="font-normal text-slate-500">({outro.nome_publico} no anúncio)</span>
          </p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {outro.email_verificado && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-200" data-testid="conv-other-email-verified"><BadgeCheck className="h-3 w-3" /> Email verificado</span>}
            {outro.telefone_verificado && <span className="inline-flex items-center gap-1 rounded-full bg-cyan-50 px-2 py-0.5 text-[10px] font-semibold text-cyan-700 ring-1 ring-cyan-200" data-testid="conv-other-phone-verified"><BadgeCheck className="h-3 w-3" /> Telefone verificado</span>}
          </div>
          <p className="mt-2 text-[11px] text-slate-500" data-testid="conv-identity-note">{detail.nota_identidade}</p>
          {(outro.contacto?.telefone || outro.contacto?.email) && (
            <div className="mt-2 flex flex-wrap gap-3 text-[12px] font-semibold text-emerald-700" data-testid="conv-other-contact">
              {outro.contacto?.telefone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {outro.contacto.telefone}</span>}
              {outro.contacto?.email && <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {outro.contacto.email}</span>}
            </div>
          )}
        </div>

        {detail.aviso_estado && (
          <p className="mt-2 inline-flex items-start gap-1.5 rounded-lg bg-amber-50 p-2 text-[11px] text-amber-800" data-testid="conv-listing-notice">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5" /> {detail.aviso_estado}
          </p>
        )}
      </div>

      <div className="max-h-[52vh] space-y-2 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-3" data-testid="conv-messages">
        {messages.length === 0 && <p className="py-8 text-center text-xs text-slate-400" data-testid="conv-messages-empty">Ainda não existem mensagens.</p>}
        {messages.map((m) => (
          m.tipo === 'sistema' ? (
            <p key={m.id} className="mx-auto w-fit rounded-full bg-slate-100 px-3 py-1 text-[11px] text-slate-500" data-testid={`conv-msg-system-${m.id}`}>{m.texto}</p>
          ) : (
            <div key={m.id} className={`flex ${m.minha ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[84%] rounded-2xl px-3 py-2 text-sm ${m.minha ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-800'}`} data-testid={`conv-msg-${m.id}`}>
                <p className="whitespace-pre-wrap break-words">{m.texto}</p>
                <p className={`mt-1 text-[10px] ${m.minha ? 'text-emerald-50/80' : 'text-slate-400'}`}>{hora(m.created_at)}</p>
              </div>
            </div>
          )
        ))}
        <div ref={bottom} />
      </div>

      {detail.pode_enviar ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-3">
          <textarea rows={3} value={texto} onChange={(e) => setTexto(e.target.value)}
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            placeholder="Escreva a sua mensagem (apenas texto)" data-testid="conv-composer" />
          {confirmar && (
            <div className="mt-2 rounded-xl border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800" data-testid="conv-confirm-share">
              <p className="font-semibold">{confirmar}</p>
              <div className="mt-2 flex gap-2">
                <button type="button" onClick={() => send(true)} className="rounded-lg bg-amber-600 px-3 py-1.5 font-semibold text-white" data-testid="conv-confirm-share-yes">Confirmar e enviar</button>
                <button type="button" onClick={() => setConfirmar(null)} className="rounded-lg border border-amber-300 px-3 py-1.5 font-semibold" data-testid="conv-confirm-share-no">Editar</button>
              </div>
            </div>
          )}
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <button type="button" onClick={() => setShowShare((s) => !s)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700"
              data-testid="conv-share-open"><Share2 className="h-3.5 w-3.5" /> Partilhar o meu contacto</button>
            <button type="button" onClick={() => send(false)} disabled={busy || !texto.trim()}
              className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              data-testid="conv-send">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Enviar</button>
          </div>
          {aviso && (
            <p className="mt-2 flex items-start justify-between gap-2 rounded-lg bg-slate-50 p-2 text-[11px] text-slate-600" data-testid="conv-send-warning">
              <span>{aviso}</span>
              <button type="button" onClick={() => setAviso('')} className="shrink-0 font-semibold text-slate-400 hover:text-slate-700" data-testid="conv-send-warning-dismiss">Dispensar</button>
            </p>
          )}
          {err && <p className="mt-2 text-xs font-semibold text-red-600" data-testid="conv-send-error">{err}</p>}
        </div>
      ) : (
        <p className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-xs font-semibold text-slate-600" data-testid="conv-cannot-send">
          Não é possível enviar novas mensagens nesta conversa.
        </p>
      )}

      {showShare && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-3 text-xs" data-testid="conv-share-panel">
          <p className="font-semibold text-slate-800">Escolha o que quer revelar ao outro participante</p>
          <label className="mt-2 flex items-center gap-2" data-testid="conv-share-phone-label">
            <input type="checkbox" checked={share.telefone} onChange={(e) => setShare((s) => ({ ...s, telefone: e.target.checked }))} data-testid="conv-share-phone" />
            Telemóvel {eu.contacto_partilhado?.telefone ? '(já partilhado)' : ''}
          </label>
          <label className="mt-1 flex items-center gap-2" data-testid="conv-share-email-label">
            <input type="checkbox" checked={share.email} onChange={(e) => setShare((s) => ({ ...s, email: e.target.checked }))} data-testid="conv-share-email" />
            Email {eu.contacto_partilhado?.email ? '(já partilhado)' : ''}
          </label>
          <p className="mt-2 text-[11px] text-slate-600">Só pode partilhar os seus próprios contactos, e apenas nesta conversa.</p>
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={submitShare} disabled={busy || (!share.telefone && !share.email)}
              className="rounded-lg bg-emerald-600 px-3 py-1.5 font-semibold text-white disabled:opacity-60" data-testid="conv-share-confirm">Confirmar partilha</button>
            <button type="button" onClick={() => setShowShare(false)} className="rounded-lg border border-slate-300 px-3 py-1.5 font-semibold" data-testid="conv-share-cancel">Cancelar</button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-3 text-xs">
        <button type="button" onClick={() => setReport((r) => ({ ...r, open: !r.open }))}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 font-semibold text-slate-700" data-testid="conv-report-open">
          <Flag className="h-3.5 w-3.5" /> Denunciar
        </button>
        <button type="button" onClick={() => act('bloquear', { bloquear: !eu.bloqueei })} disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 font-semibold text-slate-700" data-testid="conv-block">
          <Ban className="h-3.5 w-3.5" /> {eu.bloqueei ? 'Desbloquear participante' : 'Bloquear participante'}
        </button>
        {detail.estado !== 'encerrada' && (
          <button type="button" onClick={() => act('estado', { acao: 'encerrar' })} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 font-semibold text-slate-700" data-testid="conv-close">
            <XCircle className="h-3.5 w-3.5" /> Encerrar negociação
          </button>
        )}
        {detail.estado !== 'resolvida' && (
          <button type="button" onClick={() => act('estado', { acao: 'resolvida' })} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 font-semibold text-slate-700" data-testid="conv-resolve">
            <CheckCircle2 className="h-3.5 w-3.5" /> Marcar como resolvida
          </button>
        )}
        {detail.estado === 'encerrada' && (
          <button type="button" onClick={() => act('estado', { acao: 'reabrir' })} disabled={busy}
            className="rounded-lg border border-slate-300 px-3 py-1.5 font-semibold text-slate-700" data-testid="conv-reopen">Reabrir conversa</button>
        )}
      </div>

      {report.open && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50/60 p-3 text-xs" data-testid="conv-report-panel">
          <p className="font-semibold text-slate-800">Denunciar esta conversa</p>
          <select value={report.motivo} onChange={(e) => setReport((r) => ({ ...r, motivo: e.target.value }))}
            className="mt-2 w-full rounded-lg border border-slate-300 px-2 py-1.5" data-testid="conv-report-reason">
            {MOTIVOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <textarea rows={3} value={report.descricao} onChange={(e) => setReport((r) => ({ ...r, descricao: e.target.value }))}
            className="mt-2 w-full rounded-lg border border-slate-300 px-2 py-1.5" placeholder="Descreva o que aconteceu (opcional)" data-testid="conv-report-text" />
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={submitReport} disabled={busy}
              className="rounded-lg bg-rose-600 px-3 py-1.5 font-semibold text-white disabled:opacity-60" data-testid="conv-report-submit">Enviar denúncia</button>
            <button type="button" onClick={() => setReport((r) => ({ ...r, open: false }))}
              className="rounded-lg border border-slate-300 px-3 py-1.5 font-semibold" data-testid="conv-report-cancel">Cancelar</button>
          </div>
        </div>
      )}
      {report.done && <p className="text-xs font-semibold text-emerald-700" data-testid="conv-report-done">{report.done}</p>}
    </div>
  );
}
